import type { BrandProfileView, Tenant } from '@mg/shared';
import { Card, CardBody, CardHeader } from '../../../components/ui/Card';
import { EDIT_IMPORT_POLL_MS, useLatestBrandImport } from '../../brand/queries';
import { LegacyGuidancePanel } from './LegacyGuidancePanel';
import { MigrationNotice } from './MigrationNotice';
import { OnboardingNotice } from './OnboardingNotice';
import { hasSources, SourcesList } from './SourcesList';

export const BRAND_INFORMATION_CARD = {
  title: 'Brand information',
  description:
    "Your brand's details are updated through Brand Setup. You can edit Vocabulary and Writing guidelines on this page.",
} as const;

/**
 * The Brand information card (spec:178, :184, :188), between Brand Workspace Details and the section
 * cards, not a side-menu entry. In order:
 * - the migration notice (admins, on a migrated workspace until dismissed);
 * - the onboarding notice box with Start, Continue or Revisit Brand Setup (admins only, E30);
 * - the sources of the last onboarding, once it has started (the latest import, re-read every 5 s while
 *   it runs);
 * - "Previous guidance (not yet sorted)" while there is any (Copy for all, Dismiss for admins).
 */
export function BrandInformationCard({
  view,
  workspace,
  canEdit,
  onView,
}: {
  view: BrandProfileView;
  workspace: Tenant;
  canEdit: boolean;
  /** A dismissal answered with this view. */
  onView: (next: BrandProfileView) => void;
}) {
  const started = view.onboarding.status !== 'NOT_STARTED';
  // The latest import is read only once onboarding has started: before that there is nothing to list.
  const latest = useLatestBrandImport(started ? workspace.id : null, { activePollMs: EDIT_IMPORT_POLL_MS });
  const job = latest.data?.job ?? null;
  const showSources = started && job !== null && hasSources(job);
  const showMigrationNotice = canEdit && view.migration.showNotice;
  const legacyGuidance = view.legacyGuidance?.trim() ? view.legacyGuidance : null;
  const hasBody = showMigrationNotice || canEdit || showSources || legacyGuidance !== null;

  return (
    <Card>
      <CardHeader title={BRAND_INFORMATION_CARD.title} description={BRAND_INFORMATION_CARD.description} />
      {hasBody && (
        <CardBody className="space-y-4">
          {showMigrationNotice && <MigrationNotice tenantId={workspace.id} onDismissed={onView} />}
          {canEdit && <OnboardingNotice status={view.onboarding.status} slug={workspace.slug} />}
          {showSources && job && <SourcesList tenantId={workspace.id} job={job} />}
          {legacyGuidance !== null && (
            <LegacyGuidancePanel
              tenantId={workspace.id}
              text={legacyGuidance}
              canEdit={canEdit}
              onDismissed={onView}
            />
          )}
        </CardBody>
      )}
    </Card>
  );
}
